import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { ClipboardModule } from 'ngx-clipboard';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerModule } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import pkg from '../../package.json';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './modules/auth/services/auth.service';
import { CommonInterceptorInterceptor } from './Interceptor/common-interceptor.interceptor';
import { bootstrap as bootstrapSso, ssoInstance } from './auth/sso';
import { applyPortalIdentity, syncFromSso } from './auth/auth-sync';
import { CurrentUser } from './models/portal.models';
import { environment } from 'src/environments/environment';

const appVersion = pkg.version;

// Locale JSONs live in assets/i18n/{lang}.json; the ?v= suffix busts caches per release.
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${appVersion}`);
}

/**
 * Boot: silent SSO check → mirror token → ask the API who we are (role + tenant)
 * → hydrate the session. Failing `/me` leaves the token-derived role in place;
 * the RoleGuard then decides.
 */
function appInitializer(authService: AuthService, http: HttpClient) {
  return () =>
    new Promise<void>(async (resolve) => {
      await bootstrapSso();
      const kc = ssoInstance();
      if (kc?.authenticated) {
        syncFromSso();
        try {
          const me = await firstValueFrom(http.get<CurrentUser>(`${environment.apiUrl}/me`));
          applyPortalIdentity(me);
        } catch {
          // API unreachable or token rejected: keep the token-derived hint.
        }
      }
      authService.getUserByToken().subscribe().add(() => resolve());
    });
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      defaultLanguage: 'es',
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
    }),
    HttpClientModule,
    ClipboardModule,
    AppRoutingModule,
    InlineSVGModule.forRoot(),
    NgbModule,
    NgxSpinnerModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CommonInterceptorInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AuthService, HttpClient] },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
